import {
  GENERATOR_VERSION,
  assertHoleCue,
  assertPieceCue,
  generateLegacyCues,
  normalizeSeed,
} from "./cues";
import { canonicalStringify, sha256 } from "./hash";
import {
  POSE_ROWS,
  assertBoard,
  canPlace,
  cloneBoard,
  createEmptyBoard,
  findLegacyRotation,
  getPoseCells,
} from "./poses";
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  SPAWN_X,
  SPAWN_Y,
  type ActivePiece,
  type Board,
  type Cell,
  type GameEvent,
  type GameSnapshot,
  type HeldInputs,
  type InputControl,
  type LegacyGameOptions,
  type LockEvent,
  type PieceId,
  type PiecePoseEvent,
  type Rotation,
  type RotationDirection,
  type ScheduledAttack,
  type ScheduledAttackInput,
  type TopOutEvent,
} from "./types";

export const RULES_VERSION = "BWWK_BATTLE_2011_WEB_V4" as const;
export const TICKS_PER_SECOND = 120 as const;
export const INPUT_SAMPLE_TICKS = 5 as const;
export const POSE_INTERVAL_TICKS = 18 as const;
export const SPEED_LEVEL_TICKS = 7_200 as const;
export const DEFAULT_ATTACK_DELAY_TICKS = 60 as const;
export const GRAVITY_INTERVAL_TICKS = [60, 48, 36, 30, 24] as const;
export const ATTACK_LINES_BY_CLEAR = [0, 0, 1, 2, 4] as const;

export const RULES_MANIFEST = {
  rulesVersion: RULES_VERSION,
  board: { width: BOARD_WIDTH, height: BOARD_HEIGHT, spawnX: SPAWN_X, spawnY: SPAWN_Y },
  topOutFrame: { minX: 4, maxX: 7, minY: 1, maxY: 4 },
  poseRows: POSE_ROWS,
  rotation: { kickX: [0, -1, -2, 1, 2], downFallback: 1 },
  input: {
    ticksPerSecond: TICKS_PER_SECOND,
    sampleTicks: INPUT_SAMPLE_TICKS,
    horizontalRepeatTicks: 10,
    hardDrop: { attemptsPerSample: [1, 2], averageMultiplier: 1.5 },
  },
  gravity: { intervals: GRAVITY_INTERVAL_TICKS, levelTicks: SPEED_LEVEL_TICKS, blockedAttemptsToLock: 2 },
  attack: { byClearedLines: ATTACK_LINES_BY_CLEAR, defaultDelayTicks: DEFAULT_ATTACK_DELAY_TICKS, cancellation: false },
  cues: {
    pieceLength: 500,
    holeLength: 100,
    holePolicy: "seeded-random-per-row",
    generatorVersion: GENERATOR_VERSION,
  },
} as const;

export const RULES_HASH = sha256(canonicalStringify(RULES_MANIFEST));

const EMPTY_INPUTS: HeldInputs = {
  left: false,
  right: false,
  softDrop: false,
  hardDrop: false,
  rotateCW: false,
  rotateCCW: false,
};

function assertSafeInteger(value: number, name: string, minimum = 0): void {
  if (!Number.isSafeInteger(value) || value < minimum) {
    throw new RangeError(`${name} must be a safe integer >= ${minimum}`);
  }
}

function isPieceId(value: unknown): value is PieceId {
  return Number.isInteger(value) && typeof value === "number" && value >= 1 && value <= 7;
}

function isRotation(value: unknown): value is Rotation {
  return Number.isInteger(value) && typeof value === "number" && value >= 0 && value <= 3;
}

function cloneActive(active: ActivePiece): ActivePiece {
  return { ...active };
}

function cloneInputs(inputs: HeldInputs): HeldInputs {
  return { ...inputs };
}

function cloneAttack(attack: ScheduledAttack): ScheduledAttack {
  return { ...attack };
}

function compareAttacks(left: ScheduledAttack, right: ScheduledAttack): number {
  return left.eligibleTick - right.eligibleTick
    || left.createdTick - right.createdTick
    || left.sourceLockSeq - right.sourceLockSeq
    || (left.attackId < right.attackId ? -1 : left.attackId > right.attackId ? 1 : 0);
}

export function gravityLevelAtElapsedTick(elapsedTick: number): number {
  assertSafeInteger(elapsedTick, "elapsedTick");
  return Math.min(GRAVITY_INTERVAL_TICKS.length - 1, Math.floor(elapsedTick / SPEED_LEVEL_TICKS));
}

export function gravityIntervalAtElapsedTick(elapsedTick: number): number {
  return GRAVITY_INTERVAL_TICKS[gravityLevelAtElapsedTick(elapsedTick)];
}

export function attackForClearedLines(clearedLines: number): number {
  if (!Number.isInteger(clearedLines) || clearedLines < 0 || clearedLines > 4) {
    throw new RangeError("clearedLines must be an integer in 0...4");
  }
  return ATTACK_LINES_BY_CLEAR[clearedLines];
}

export interface ClearedBoard {
  board: Board;
  clearedLines: number;
}

export function clearCompletedLines(source: readonly (readonly Cell[])[]): ClearedBoard {
  assertBoard(source);
  const survivingRows = source.filter((row) => row.some((cell) => cell === 0));
  const clearedLines = BOARD_HEIGHT - survivingRows.length;
  const board: Board = Array.from({ length: clearedLines }, () => Array<Cell>(BOARD_WIDTH).fill(0));
  for (const row of survivingRows) board.push([...row]);
  return { board, clearedLines };
}

export interface GarbageResult {
  board: Board;
  holeIndex: number;
}

export function appendGarbageRows(
  source: readonly (readonly Cell[])[],
  lines: number,
  holeCue: readonly number[],
  initialHoleIndex: number,
): GarbageResult {
  assertBoard(source);
  assertHoleCue(holeCue);
  assertSafeInteger(lines, "lines");
  assertSafeInteger(initialHoleIndex, "initialHoleIndex");
  const board = cloneBoard(source);
  let holeIndex = initialHoleIndex;

  for (let line = 0; line < lines; line += 1) {
    const hole = holeCue[holeIndex % holeCue.length];
    board.shift();
    const garbageRow = Array<Cell>(BOARD_WIDTH).fill(8);
    garbageRow[hole] = 0;
    board.push(garbageRow);
    holeIndex += 1;
  }

  return { board, holeIndex };
}

/** Final-state loss frame from the document: x=4...7 and y=1...4. */
export function isTopOutBoard(board: readonly (readonly Cell[])[]): boolean {
  assertBoard(board);
  for (let y = 0; y < 4; y += 1) {
    for (let x = 3; x < 7; x += 1) {
      if (board[y][x] !== 0) return true;
    }
  }
  return false;
}

export function boardToBytes(board: readonly (readonly Cell[])[]): Uint8Array {
  assertBoard(board);
  const bytes = new Uint8Array(BOARD_WIDTH * BOARD_HEIGHT);
  let index = 0;
  for (const row of board) {
    for (const cell of row) bytes[index++] = cell;
  }
  return bytes;
}

export function boardFromBytes(bytes: Uint8Array): Board {
  if (bytes.length !== BOARD_WIDTH * BOARD_HEIGHT) {
    throw new RangeError(`Board byte snapshots must contain ${BOARD_WIDTH * BOARD_HEIGHT} bytes`);
  }
  const board = createEmptyBoard();
  for (let index = 0; index < bytes.length; index += 1) {
    const value = bytes[index];
    if (value > 8) throw new RangeError("Board bytes must contain cell values in 0...8");
    board[Math.floor(index / BOARD_WIDTH)][index % BOARD_WIDTH] = value as Cell;
  }
  return board;
}

export function hashBoard(board: readonly (readonly Cell[])[]): string {
  return sha256(boardToBytes(board));
}

export function serializeSnapshot(snapshot: GameSnapshot): string {
  return canonicalStringify(snapshot);
}

export function hashSnapshot(snapshot: GameSnapshot): string {
  return sha256(serializeSnapshot(snapshot));
}

export function deserializeSnapshot(serialized: string): GameSnapshot {
  const parsed: unknown = JSON.parse(serialized);
  return LegacyGame.fromSnapshot(parsed as GameSnapshot).snapshot();
}

export class LegacyGame {
  private _matchId: string;
  private _seed: string;
  private _startTick: number;
  private _tick: number;
  private _board: Board;
  private _active: ActivePiece;
  private _blockIndex: number;
  private _holeIndex: number;
  private _pieceCue: PieceId[];
  private _holeCue: number[];
  private _pendingGarbage: ScheduledAttack[];
  private _seenAttackIds: Set<string>;
  private _gravityLevel: number;
  private _nextGravityTick: number;
  private _blockedDownAttempts: number;
  private _heldInputs: HeldInputs;
  private _hardDropPhase: 0 | 1;
  private _rotationLatch: boolean;
  private _leftCounter: number;
  private _rightCounter: number;
  private _lastInputSeq: number;
  private _lastLockSeq: number;
  private _topOut: boolean;
  private readonly onEvent?: (event: GameEvent) => void;

  constructor(options: LegacyGameOptions = {}) {
    const startTick = options.startTick ?? 0;
    assertSafeInteger(startTick, "startTick");
    const seed = normalizeSeed(options.seed ?? 0);
    const generated = generateLegacyCues(seed);
    const pieceCue = options.pieceCue ? [...options.pieceCue] : generated.pieceCue;
    const holeCue = options.holeCue ? [...options.holeCue] : generated.holeCue;
    assertPieceCue(pieceCue);
    assertHoleCue(holeCue);

    this._matchId = options.matchId ?? "";
    this._seed = seed;
    this._startTick = startTick;
    this._tick = startTick;
    this._board = createEmptyBoard();
    this._pieceCue = [...pieceCue];
    this._holeCue = [...holeCue];
    this._blockIndex = 0;
    this._holeIndex = 0;
    this._active = this.spawnForBlockIndex(0);
    this._pendingGarbage = [];
    this._seenAttackIds = new Set();
    this._gravityLevel = 0;
    this._nextGravityTick = startTick + GRAVITY_INTERVAL_TICKS[0];
    this._blockedDownAttempts = 0;
    this._heldInputs = { ...EMPTY_INPUTS };
    this._hardDropPhase = 0;
    this._rotationLatch = false;
    this._leftCounter = 0;
    this._rightCounter = 0;
    this._lastInputSeq = 0;
    this._lastLockSeq = 0;
    this._topOut = false;
    this.onEvent = options.onEvent;
  }

  static fromSnapshot(snapshot: GameSnapshot, onEvent?: (event: GameEvent) => void): LegacyGame {
    const game = new LegacyGame({
      matchId: snapshot.matchId,
      startTick: snapshot.startTick,
      seed: snapshot.seed,
      pieceCue: snapshot.pieceCue,
      holeCue: snapshot.holeCue,
      onEvent,
    });
    game.restore(snapshot);
    return game;
  }

  get matchId(): string { return this._matchId; }
  get startTick(): number { return this._startTick; }
  get tick(): number { return this._tick; }
  get board(): Board { return cloneBoard(this._board); }
  get active(): ActivePiece { return cloneActive(this._active); }
  get blockIndex(): number { return this._blockIndex; }
  get holeIndex(): number { return this._holeIndex; }
  get nextPiece(): PieceId { return this._pieceCue[(this._blockIndex + 1) % this._pieceCue.length]; }
  get gravityLevel(): number { return this._gravityLevel; }
  get nextGravityTick(): number { return this._nextGravityTick; }
  get blockedDownAttempts(): number { return this._blockedDownAttempts; }
  get heldInputs(): HeldInputs { return cloneInputs(this._heldInputs); }
  get hardDropPhase(): 0 | 1 { return this._hardDropPhase; }
  get rotationLatch(): boolean { return this._rotationLatch; }
  get leftCounter(): number { return this._leftCounter; }
  get rightCounter(): number { return this._rightCounter; }
  get lastInputSeq(): number { return this._lastInputSeq; }
  get lastLockSeq(): number { return this._lastLockSeq; }
  get topOut(): boolean { return this._topOut; }
  /** Original battle score path is dead code and remains zero. */
  get score(): 0 { return 0; }
  get pendingGarbage(): ScheduledAttack[] { return this._pendingGarbage.map(cloneAttack); }
  get pendingGarbageLines(): number { return this._pendingGarbage.reduce((sum, attack) => sum + attack.lines, 0); }

  eligibleGarbageLines(atTick = this._tick): number {
    assertSafeInteger(atTick, "atTick");
    return this._pendingGarbage
      .filter((attack) => attack.eligibleTick <= atTick)
      .reduce((sum, attack) => sum + attack.lines, 0);
  }

  currentPoseEvent(): PiecePoseEvent {
    return {
      type: "pose",
      tick: this._tick,
      blockIndex: this._blockIndex,
      piece: this._active.piece,
      rotation: this._active.rotation,
      x: this._active.x,
      y: this._active.y,
    };
  }

  /**
   * Updates held controls. When inputSeq is supplied, stale/duplicate batches
   * are rejected without changing state.
   */
  setInputs(inputs: Partial<HeldInputs>, inputSeq?: number): boolean {
    if (inputSeq !== undefined) {
      assertSafeInteger(inputSeq, "inputSeq");
      if (inputSeq <= this._lastInputSeq) return false;
    }
    for (const key of Object.keys(inputs) as InputControl[]) {
      const value = inputs[key];
      if (typeof value !== "boolean") throw new TypeError(`${key} must be boolean`);
      this._heldInputs[key] = value;
      if (key === "hardDrop" && !value) this._hardDropPhase = 0;
    }
    if (inputSeq !== undefined) this._lastInputSeq = inputSeq;
    return true;
  }

  setInput(control: InputControl, pressed: boolean, inputSeq?: number): boolean {
    if (!(control in EMPTY_INPUTS)) throw new RangeError(`Unknown input control: ${control}`);
    return this.setInputs({ [control]: pressed }, inputSeq);
  }

  /** Adds a deduplicated incoming attack to the future lock-boundary queue. */
  applyScheduledAttack(input: ScheduledAttackInput): boolean {
    if (!input.attackId) throw new RangeError("attackId must be non-empty");
    assertSafeInteger(input.lines, "attack lines", 1);
    assertSafeInteger(input.eligibleTick, "eligibleTick");
    if (this._seenAttackIds.has(input.attackId)) return false;

    const attack: ScheduledAttack = {
      attackId: input.attackId,
      lines: input.lines,
      eligibleTick: input.eligibleTick,
      createdTick: input.createdTick ?? this._tick,
      sourceLockSeq: input.sourceLockSeq ?? 0,
    };
    assertSafeInteger(attack.createdTick, "createdTick");
    assertSafeInteger(attack.sourceLockSeq, "sourceLockSeq");
    this._seenAttackIds.add(attack.attackId);
    this._pendingGarbage.push(attack);
    this._pendingGarbage.sort(compareAttacks);
    return true;
  }

  step(ticks = 1): GameEvent[] {
    assertSafeInteger(ticks, "ticks");
    return this.stepTo(this._tick + ticks);
  }

  stepTo(targetTick: number): GameEvent[] {
    assertSafeInteger(targetTick, "targetTick");
    if (targetTick < this._tick) throw new RangeError("LegacyGame cannot step backward; restore a checkpoint instead");
    const events: GameEvent[] = [];
    while (this._tick < targetTick) this.advanceOneTick(events);
    return events;
  }

  snapshot(): GameSnapshot {
    return {
      snapshotVersion: 1,
      rulesVersion: RULES_VERSION,
      rulesHash: RULES_HASH,
      generatorVersion: GENERATOR_VERSION,
      matchId: this._matchId,
      seed: this._seed,
      startTick: this._startTick,
      tick: this._tick,
      board: cloneBoard(this._board),
      active: cloneActive(this._active),
      blockIndex: this._blockIndex,
      holeIndex: this._holeIndex,
      pieceCue: [...this._pieceCue],
      holeCue: [...this._holeCue],
      pendingGarbage: this._pendingGarbage.map(cloneAttack),
      seenAttackIds: [...this._seenAttackIds].sort(),
      gravityLevel: this._gravityLevel,
      nextGravityTick: this._nextGravityTick,
      blockedDownAttempts: this._blockedDownAttempts,
      heldInputs: cloneInputs(this._heldInputs),
      hardDropPhase: this._hardDropPhase,
      rotationLatch: this._rotationLatch,
      leftCounter: this._leftCounter,
      rightCounter: this._rightCounter,
      lastInputSeq: this._lastInputSeq,
      lastLockSeq: this._lastLockSeq,
      topOut: this._topOut,
    };
  }

  stateHash(): string {
    return hashSnapshot(this.snapshot());
  }

  restore(snapshot: GameSnapshot): void {
    if (!snapshot || snapshot.snapshotVersion !== 1) throw new RangeError("Unsupported game snapshot version");
    if (snapshot.rulesVersion !== RULES_VERSION || snapshot.rulesHash !== RULES_HASH) {
      throw new RangeError("Snapshot rules do not match this engine");
    }
    if (snapshot.generatorVersion !== GENERATOR_VERSION) throw new RangeError("Snapshot cue generator version does not match");
    assertSafeInteger(snapshot.startTick, "snapshot.startTick");
    assertSafeInteger(snapshot.tick, "snapshot.tick");
    if (snapshot.tick < snapshot.startTick) throw new RangeError("Snapshot tick precedes startTick");
    assertBoard(snapshot.board);
    assertPieceCue(snapshot.pieceCue);
    assertHoleCue(snapshot.holeCue);
    if (!isPieceId(snapshot.active?.piece) || !isRotation(snapshot.active?.rotation)) {
      throw new RangeError("Snapshot active piece or rotation is invalid");
    }
    assertSafeInteger(snapshot.active.x, "snapshot.active.x", -3);
    assertSafeInteger(snapshot.active.y, "snapshot.active.y", -3);
    assertSafeInteger(snapshot.blockIndex, "snapshot.blockIndex");
    assertSafeInteger(snapshot.holeIndex, "snapshot.holeIndex");
    assertSafeInteger(snapshot.gravityLevel, "snapshot.gravityLevel");
    if (snapshot.gravityLevel !== gravityLevelAtElapsedTick(snapshot.tick - snapshot.startTick)) {
      throw new RangeError("Snapshot gravityLevel is inconsistent with its tick");
    }
    assertSafeInteger(snapshot.nextGravityTick, "snapshot.nextGravityTick");
    assertSafeInteger(snapshot.blockedDownAttempts, "snapshot.blockedDownAttempts");
    assertSafeInteger(snapshot.leftCounter, "snapshot.leftCounter");
    assertSafeInteger(snapshot.rightCounter, "snapshot.rightCounter");
    assertSafeInteger(snapshot.lastInputSeq, "snapshot.lastInputSeq");
    assertSafeInteger(snapshot.lastLockSeq, "snapshot.lastLockSeq");
    if (snapshot.hardDropPhase !== 0 && snapshot.hardDropPhase !== 1) {
      throw new RangeError("Snapshot hardDropPhase is invalid");
    }
    if (typeof snapshot.topOut !== "boolean" || typeof snapshot.rotationLatch !== "boolean") {
      throw new RangeError("Snapshot boolean state is invalid");
    }
    for (const key of Object.keys(EMPTY_INPUTS) as InputControl[]) {
      if (typeof snapshot.heldInputs?.[key] !== "boolean") throw new RangeError("Snapshot heldInputs are invalid");
    }
    if (!Array.isArray(snapshot.seenAttackIds) || !snapshot.seenAttackIds.every((id) => typeof id === "string" && id.length > 0)) {
      throw new RangeError("Snapshot seenAttackIds are invalid");
    }
    if (new Set(snapshot.seenAttackIds).size !== snapshot.seenAttackIds.length) {
      throw new RangeError("Snapshot seenAttackIds must be unique");
    }
    if (!Array.isArray(snapshot.pendingGarbage)) throw new RangeError("Snapshot pendingGarbage is invalid");
    for (const attack of snapshot.pendingGarbage) {
      if (!attack || typeof attack.attackId !== "string" || !attack.attackId) throw new RangeError("Snapshot attackId is invalid");
      assertSafeInteger(attack.lines, "snapshot attack lines", 1);
      assertSafeInteger(attack.eligibleTick, "snapshot attack eligibleTick");
      assertSafeInteger(attack.createdTick, "snapshot attack createdTick");
      assertSafeInteger(attack.sourceLockSeq, "snapshot attack sourceLockSeq");
      if (!snapshot.seenAttackIds.includes(attack.attackId)) throw new RangeError("Pending attacks must be present in seenAttackIds");
    }

    const board = cloneBoard(snapshot.board);
    const active = cloneActive(snapshot.active);
    if (!snapshot.topOut && !canPlace(board, active)) throw new RangeError("Snapshot active piece collides with its board");

    this._matchId = String(snapshot.matchId);
    this._seed = String(snapshot.seed);
    this._startTick = snapshot.startTick;
    this._tick = snapshot.tick;
    this._board = board;
    this._active = active;
    this._blockIndex = snapshot.blockIndex;
    this._holeIndex = snapshot.holeIndex;
    this._pieceCue = [...snapshot.pieceCue];
    this._holeCue = [...snapshot.holeCue];
    this._pendingGarbage = snapshot.pendingGarbage.map(cloneAttack).sort(compareAttacks);
    this._seenAttackIds = new Set(snapshot.seenAttackIds);
    this._gravityLevel = snapshot.gravityLevel;
    this._nextGravityTick = snapshot.nextGravityTick;
    this._blockedDownAttempts = snapshot.blockedDownAttempts;
    this._heldInputs = cloneInputs(snapshot.heldInputs);
    this._hardDropPhase = snapshot.hardDropPhase;
    this._rotationLatch = snapshot.rotationLatch;
    this._leftCounter = snapshot.leftCounter;
    this._rightCounter = snapshot.rightCounter;
    this._lastInputSeq = snapshot.lastInputSeq;
    this._lastLockSeq = snapshot.lastLockSeq;
    this._topOut = snapshot.topOut;
  }

  private spawnForBlockIndex(blockIndex: number): ActivePiece {
    return {
      piece: this._pieceCue[blockIndex % this._pieceCue.length],
      rotation: 0,
      x: SPAWN_X,
      y: SPAWN_Y,
    };
  }

  private emit(event: GameEvent, events: GameEvent[]): void {
    events.push(event);
    this.onEvent?.(event);
  }

  private advanceOneTick(events: GameEvent[]): void {
    this._tick += 1;
    const elapsedTick = this._tick - this._startTick;
    this._gravityLevel = gravityLevelAtElapsedTick(elapsedTick);
    let lockedThisTick = false;

    if (!this._topOut && elapsedTick > 0 && elapsedTick % INPUT_SAMPLE_TICKS === 0) {
      lockedThisTick = this.processInputSample(events);
    }

    while (this._tick >= this._nextGravityTick) {
      const dueElapsedTick = Math.max(0, this._nextGravityTick - this._startTick);
      this._nextGravityTick += gravityIntervalAtElapsedTick(dueElapsedTick);
      if (!this._topOut && !lockedThisTick) {
        lockedThisTick = this.attemptDown(events);
      }
    }

    if (!this._topOut && elapsedTick > 0 && elapsedTick % POSE_INTERVAL_TICKS === 0) {
      this.emit(this.currentPoseEvent(), events);
    }
  }

  /** Returns true if this sample fixed a piece. */
  private processInputSample(events: GameEvent[]): boolean {
    if (this._heldInputs.right) {
      this._rightCounter += 1;
      if (this._rightCounter % 2 === 1) this.tryHorizontalMove(1);
    }
    if (this._heldInputs.left) {
      this._leftCounter += 1;
      if (this._leftCounter % 2 === 1) this.tryHorizontalMove(-1);
    }

    if (!this._heldInputs.rotateCW && !this._heldInputs.rotateCCW) {
      this._rotationLatch = false;
    } else if (!this._rotationLatch) {
      const direction: RotationDirection = this._heldInputs.rotateCW ? "cw" : "ccw";
      this.tryRotation(direction);
      this._rotationLatch = true;
    }

    if (this._heldInputs.hardDrop && !this._topOut) {
      const attempts = this._hardDropPhase === 0 ? 1 : 2;
      this._hardDropPhase = this._hardDropPhase === 0 ? 1 : 0;
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        if (this.attemptDown(events)) return true;
      }
      return false;
    }
    this._hardDropPhase = 0;
    if (this._heldInputs.softDrop && !this._topOut) return this.attemptDown(events);
    return false;
  }

  private tryHorizontalMove(offset: -1 | 1): boolean {
    const candidate = { ...this._active, x: this._active.x + offset };
    if (!canPlace(this._board, candidate)) return false;
    this._active = candidate;
    return true;
  }

  private tryRotation(direction: RotationDirection): boolean {
    const candidate = findLegacyRotation(this._board, this._active, direction);
    if (!candidate) return false;
    this._active = candidate;
    return true;
  }

  /** Returns true when the second blocked attempt fixed the piece. */
  private attemptDown(events: GameEvent[]): boolean {
    const candidate = { ...this._active, y: this._active.y + 1 };
    if (canPlace(this._board, candidate)) {
      this._active = candidate;
      this._blockedDownAttempts = 0;
      return false;
    }

    this._blockedDownAttempts += 1;
    if (this._blockedDownAttempts < 2) return false;
    this.lockActivePiece(events);
    return true;
  }

  private lockActivePiece(events: GameEvent[]): void {
    // An incoming garbage packet can move the settled stack into the spawn
    // area between two simulation steps. Treat that as an immediate top out
    // instead of throwing from the animation loop and leaving the match frozen.
    if (!canPlace(this._board, this._active)) {
      this._topOut = true;
      const stateHash = this.stateHash();
      this.emit({
        type: "topout",
        tick: this._tick,
        lockSeq: this._lastLockSeq,
        stateHash,
      }, events);
      return;
    }
    const locked = cloneActive(this._active);
    const lockedBlockIndex = this._blockIndex;
    const boardWithPiece = cloneBoard(this._board);
    for (const { x, y } of getPoseCells(locked)) boardWithPiece[y - 1][x - 1] = locked.piece;

    const cleared = clearCompletedLines(boardWithPiece);
    const attackLines = attackForClearedLines(cleared.clearedLines);

    const eligibleAttacks = this._pendingGarbage.filter((attack) => attack.eligibleTick <= this._tick);
    this._pendingGarbage = this._pendingGarbage.filter((attack) => attack.eligibleTick > this._tick);
    const garbageApplied = eligibleAttacks.reduce((sum, attack) => sum + attack.lines, 0);
    const garbage = appendGarbageRows(cleared.board, garbageApplied, this._holeCue, this._holeIndex);
    this._board = garbage.board;
    this._holeIndex = garbage.holeIndex;

    this._lastLockSeq += 1;
    this._blockIndex += 1;
    this._active = this.spawnForBlockIndex(this._blockIndex);
    this._blockedDownAttempts = 0;
    // Garbage is applied at the lock boundary. If it reaches the spawn
    // frame, the newly spawned piece may already collide even when the
    // settled-board loss frame is not otherwise filled.
    this._topOut = isTopOutBoard(this._board) || !canPlace(this._board, this._active);

    const stateHash = this.stateHash();
    const lockEvent: LockEvent = {
      type: "lock",
      tick: this._tick,
      lockSeq: this._lastLockSeq,
      lockedBlockIndex,
      piece: locked.piece,
      rotation: locked.rotation,
      x: locked.x,
      y: locked.y,
      board: cloneBoard(this._board),
      boardHash: hashBoard(this._board),
      stateHash,
      clearedLines: cleared.clearedLines,
      attackLines,
      garbageApplied,
      appliedAttackIds: eligibleAttacks.map((attack) => attack.attackId).sort(),
      blockIndex: this._blockIndex,
      holeIndex: this._holeIndex,
      nextPiece: this.nextPiece,
      topOut: this._topOut,
    };
    this.emit(lockEvent, events);

    if (this._topOut) {
      const topOutEvent: TopOutEvent = {
        type: "topout",
        tick: this._tick,
        lockSeq: this._lastLockSeq,
        stateHash,
      };
      this.emit(topOutEvent, events);
    }
  }
}
